import React from 'react';
import { twMerge } from 'tailwind-merge';
import MenuItem from 'flow/components/ui-components/MenuItem';
import Icon from './ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import { google, outlook, office365, ics } from 'calendar-link';
import ContextMenu from './ContextMenu';

interface Props {
    link: string;
    dark?: boolean;
}

const BookMeeting = ({ link, dark }: Props) => {
    const { i18next } = useAppContext();

    const event = {
        title: i18next.t('videoconference'),
        description: link
            ? `${i18next.t('videoconference.book_meeting.join')}: ${link}`
            : '',
        start: new Date(),
        location: link,
    };

    const teamsMeetingUrl = `https://teams.microsoft.com/l/meeting/new?subject=${encodeURIComponent(
        event.title,
    )}&startTime=${encodeURIComponent(event.start.toISOString())}&location=${encodeURIComponent(event.location)}`;

    return (
        <ContextMenu
            // rounded-xl overrides ContextMenu's default rounded-sm: without
            // it, the trigger's own rounded-xl border content mismatches the
            // MenuButton's actual (sharper) hit-box shape, most visible on
            // hover.
            className="relative flex rounded-xl bg-transparent p-0"
            icon={
                // Matches MeetingLobbyJoiningInfoButtons' "Joining info"
                // button styling, so the two sit consistently side by side.
                <div
                    className={twMerge(
                        'flex items-center gap-3 rounded-xl border px-3 py-2 text-sm font-medium text-gray-800',
                        dark &&
                            'cursor-pointer border-none bg-transparent text-white select-none',
                    )}
                >
                    <Icon iconKey="calendarAddOn" className="size-6" />
                    {i18next.t(
                        'videoconference.join_info.schedule_in_calendar',
                    )}
                </div>
            }
            preventOverflow={true}
        >
            <MenuItem>
                <a
                    href={ics(event)}
                    className="flex items-center gap-1"
                    download={true}
                >
                    <Icon iconKey="download" className="me-2 text-gray-600" />
                    <span className="text-sm">
                        {i18next.t('videoconference.book_meeting.download')}
                    </span>
                </a>
            </MenuItem>
            <MenuItem>
                <a
                    href={google(event)}
                    target="_blank"
                    className="flex items-center gap-2"
                    rel="noreferrer"
                >
                    <Icon
                        iconKey="GoogleCalendar"
                        className="me-2 text-gray-600"
                    />
                    <span className="text-sm">
                        {i18next.t('videoconference.book_meeting.google')}
                    </span>
                </a>
            </MenuItem>
            <MenuItem>
                <a
                    href={office365(event)}
                    target="_blank"
                    className="flex items-center gap-2"
                    rel="noreferrer"
                >
                    <Icon iconKey="Office365" className="me-2 text-gray-600" />
                    <span className="text-sm">
                        {i18next.t('videoconference.book_meeting.office365')}
                    </span>
                </a>
            </MenuItem>
            <MenuItem>
                <a
                    href={outlook(event)}
                    target="_blank"
                    className="flex items-center gap-2"
                    rel="noreferrer"
                >
                    <Icon iconKey="Outlook" className="me-2 text-gray-600" />
                    <span className="text-sm">
                        {i18next.t('videoconference.book_meeting.outlook')}
                    </span>
                </a>
            </MenuItem>
            <MenuItem>
                <a
                    href={teamsMeetingUrl}
                    target="_blank"
                    className="flex items-center gap-2"
                    rel="noreferrer"
                >
                    <Icon iconKey="Teams" className="me-2 text-gray-600" />
                    <span className="text-sm">
                        {i18next.t('videoconference.book_meeting.teams')}
                    </span>
                </a>
            </MenuItem>
        </ContextMenu>
    );
};

export default BookMeeting;
